import { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { useTheme } from '@/contexts/ThemeContext';
import type { SavedLesson } from '@/mocks/lessons';

interface WorksheetsProps {
  lesson: SavedLesson;
}

export default function Worksheets({ lesson }: WorksheetsProps) {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  const [expanded, setExpanded] = useState<string | null>(null);

  const t = {
    textPrimary: isDark ? 'text-white' : 'text-neutral-900',
    textMuted: isDark ? 'text-white/30' : 'text-black/30',
    textFaint: isDark ? 'text-white/25' : 'text-black/25',
    iconBg: isDark ? 'bg-white/[0.06]' : 'bg-black/[0.04]',
    iconMuted: isDark ? 'text-white/50' : 'text-black/40',
    cardBg: isDark ? 'bg-white/[0.02]' : 'bg-black/[0.02]',
    cardBorder: isDark ? 'border-white/[0.05]' : 'border-black/[0.05]',
    cardBorderHover: isDark ? 'hover:border-white/[0.10]' : 'hover:border-black/[0.10]',
    completedBg: isDark ? 'bg-emerald-500/[0.08] border-emerald-500/12' : 'bg-emerald-50 border-emerald-200/50',
    completedIcon: isDark ? 'text-emerald-400' : 'text-emerald-600',
    incompleteBg: isDark ? 'bg-white/[0.04] border-white/[0.06]' : 'bg-black/[0.04] border-black/[0.06]',
    incompleteIcon: isDark ? 'text-white/40' : 'text-black/40',
    cardDivider: isDark ? 'border-white/[0.04]' : 'border-black/[0.04]',
    progressTrack: isDark ? 'bg-white/[0.06]' : 'bg-black/[0.06]',
    progressFillDone: isDark ? 'bg-emerald-400' : 'bg-emerald-500',
    progressFill: isDark ? 'bg-white/40' : 'bg-black/30',
    btnPrimary: isDark ? 'bg-white text-black' : 'bg-black text-white',
    btnGhost: isDark ? 'bg-white/[0.04] text-white/50 border-white/[0.06]' : 'bg-black/[0.04] text-black/50 border-black/[0.06]',
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, ease: [0.32, 0.72, 0, 1] }}
      className="px-5 pb-4"
    >
      <div className="flex items-center gap-2 mb-4">
        <div className={`w-8 h-8 rounded-lg ${t.iconBg} flex items-center justify-center`}>
          <div className="w-4 h-4 flex items-center justify-center">
            <i className={`ri-draft-line ${t.iconMuted} text-sm`} />
          </div>
        </div>
        <div>
          <h3 className={`${t.textPrimary} text-sm font-semibold`}>Worksheets</h3>
          <p className={`${t.textFaint} text-[11px]`}>
            {lesson.worksheets.filter((w) => w.completed).length} of {lesson.worksheets.length} completed
          </p>
        </div>
      </div>

      <div className="space-y-2.5">
        {lesson.worksheets.map((ws, i) => {
          const isExpanded = expanded === ws.id;
          const progress = Math.floor(Math.random() * (ws.completed ? 100 : 65));

          return (
            <motion.div
              key={ws.id}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.08, duration: 0.3 }}
            >
              <motion.button
                whileTap={{ scale: 0.97 }}
                onClick={() => setExpanded(isExpanded ? null : ws.id)}
                className={`w-full text-left p-4 rounded-2xl ${t.cardBg} border ${t.cardBorder} ${t.cardBorderHover} transition-all duration-200 cursor-pointer`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                    ws.completed ? t.completedBg : t.incompleteBg
                  }`}>
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className={`${ws.completed ? `ri-check-line ${t.completedIcon}` : `ri-draft-line ${t.incompleteIcon}`} text-sm`} />
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className={`${t.textPrimary} text-sm font-semibold truncate`}>{ws.title}</h4>
                    <p className={`${t.textMuted} text-[11px]`}>{ws.problemCount} problems</p>
                  </div>
                  <div className="w-6 h-6 flex items-center justify-center">
                    {isExpanded ? (
                      <i className={`ri-arrow-up-s-line ${t.textFaint} text-sm`} />
                    ) : (
                      <i className={`ri-arrow-down-s-line ${t.textFaint} text-sm`} />
                    )}
                  </div>
                </div>

                <AnimatePresence>
                  {isExpanded && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.25 }}
                      className={`mt-3 pt-3 border-t ${t.cardDivider}`}
                    >
                      <div className="flex items-center gap-2 mb-3">
                        <span className={`${t.textMuted} text-[11px] font-medium`}>Progress</span>
                        <div className={`flex-1 h-1.5 ${t.progressTrack} rounded-full overflow-hidden`}>
                          <motion.div
                            className={`h-full rounded-full ${ws.completed ? t.progressFillDone : t.progressFill}`}
                            initial={{ width: 0 }}
                            animate={{ width: `${ws.completed ? 100 : progress}%` }}
                            transition={{ duration: 0.5, delay: 0.1 }}
                          />
                        </div>
                        <span className={`${t.textMuted} text-[11px] font-medium`}>{ws.completed ? 100 : progress}%</span>
                      </div>

                      <div className="flex gap-2">
                        <motion.button
                          whileTap={{ scale: 0.95 }}
                          className={`flex-1 py-2.5 rounded-xl ${t.btnPrimary} text-xs font-semibold cursor-pointer`}
                        >
                          {ws.completed ? 'Review' : 'Continue'}
                        </motion.button>
                        <motion.button
                          whileTap={{ scale: 0.95 }}
                          className={`flex-1 py-2.5 rounded-xl text-xs font-semibold border ${t.btnGhost} cursor-pointer`}
                        >
                          Download
                        </motion.button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.button>
            </motion.div>
          );
        })}
      </div>
    </motion.div>
  );
}